import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../redux/store";
import {
  FileText, Download, Search, Filter, Plus, Building2,
  ArrowUpRight, Clock, Calendar, DollarSign, ChevronDown,
  MoreVertical, File, ArrowRight, X, User, Mail, Calendar as CalendarIcon,
  Printer, Eye, CheckCircle, PaperclipIcon, ClipboardList,
  AlertCircleIcon, ExternalLink
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import SharedFooter3 from "../../Footer/SharedFooter3";
import { 
  fetchInvoices, 
  clearInvoicesError,
  setInvoicesSearchQuery,
  clearAllInvoices,
  updateInvoice
} from "../../redux/Slices/InvoiceSlice";
import { Invoice, InvoiceStatusEnum } from "../../modules/shared/model";
import { RoleEnum } from "../../modules/shared/model/role.enum";
import AddInvoice from "./AddInvoice";
import { InvoiceGenerator, createInvoiceData } from "../../utils/invoiceGenerator";
import { 
  downloadInvoicePdf, 
  getInvoiceById,
  getZohoInvoiceUrl,
  getUniqueInvoiceNames
} from "../../redux/Actions/InvoiceActions";
import ZohoInvoiceViewer from "../../components/invoices/ZohoInvoiceViewer";

// Helper function to check if invoice should be displayed as negative
const isCreditNote = (invoice: any): boolean => {
  // Check if it's a credit note from Zoho metadata
  if (invoice.zohoMetadata?.type === "credit_note" || invoice.invoiceType === "credit_note") {
    return true;
  }
  
  // Check if invoice number includes "CN" (Credit Note)
  if (invoice.invoiceNumber && invoice.invoiceNumber.toUpperCase().includes("CN")) {
    return true;
  }
  
  // Check if Zoho invoice number includes "CN"
  if (invoice.zohoInvoiceNumber && invoice.zohoInvoiceNumber.toUpperCase().includes("CN")) {
    return true;
  }
  
  return false;
};

// Helper function to format invoice amount
const formatInvoiceAmount = (invoice: any): string => {
  const amount = invoice.amount || 0;
  const currency = invoice.currency?.code || "USD";
  const displayAmount = isCreditNote(invoice) ? -amount : amount;
  
  return `${currency} $${displayAmount.toFixed(2)}`;
};

const InvoicesPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  
  // Redux state
  const {
    invoices,
    invoicesLoading,
    invoicesError,
    invoicesTotalCount,
    invoicesCurrentPage,
    invoicesHasNextPage,
    invoicesHasPreviousPage,
    invoicesSearchQuery
  } = useSelector((state: any) => state.invoices);

  // Local state management
  const [showFilters, setShowFilters] = useState(false);
  const [selectedType, setSelectedType] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedInvoiceName, setSelectedInvoiceName] = useState("all");
  const [invoiceNames, setInvoiceNames] = useState<string[]>([]);
  const [showZohoViewer, setShowZohoViewer] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [dateRange, setDateRange] = useState("month");
  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch unique invoice names on component mount
  useEffect(() => {
    const fetchInvoiceNames = async () => {
      try {
        const names = await getUniqueInvoiceNames();
        setInvoiceNames(names);
      } catch (error) {
        console.error("Error fetching invoice names:", error);
      }
    };
    fetchInvoiceNames();
  }, []);

  // Fetch invoices on component mount and when filters change
  useEffect(() => {
    const queryParams: Record<string, string | number | boolean> = {
      page: currentPage,
      limit: 10,
    };

    // Add search query if exists
    if (invoicesSearchQuery) {
      queryParams.search = invoicesSearchQuery;
    }

    // Add filters
    if (selectedStatus !== "all") {
      queryParams.status = selectedStatus;
    }

    if (selectedType !== "all") {
      queryParams.type = selectedType;
    }

    if (selectedInvoiceName !== "all") {
      queryParams.invoiceName = selectedInvoiceName;
    }

    // Add sorting
    if (sortBy) {
      queryParams.sort = sortBy;
    }

    dispatch(fetchInvoices(queryParams));
  }, [dispatch, currentPage, invoicesSearchQuery, selectedStatus, selectedType, selectedInvoiceName, sortBy]);


  // Handle search input change
  const handleSearchChange = (value: string) => {
    dispatch(setInvoicesSearchQuery(value));
  };

  // Handle PDF download from Zoho
  const handleDownloadPDF = async (invoice: Invoice) => {
    // Show loader
    const loadingToast = document.createElement("div");
    loadingToast.className = "fixed top-4 right-4 bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2";
    loadingToast.innerHTML = "<div class=\"animate-spin rounded-full h-5 w-5 border-b-2 border-white\"></div> Downloading PDF...";
    document.body.appendChild(loadingToast);

    try {
      // Check for zohoInvoiceId (primary check)
      if (invoice.zohoInvoiceId) {
        const blob = await downloadInvoicePdf(invoice.id);
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `invoice-${invoice.invoiceNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        // Show success message
        loadingToast.className = "fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50";
        loadingToast.innerHTML = "✓ PDF downloaded successfully!";
        setTimeout(() => loadingToast.remove(), 2000);
      } else {
        // Try to fetch fresh invoice data in case the field was missing
        try {
          const freshInvoice = await getInvoiceById(invoice.id);
          
          if (freshInvoice.zohoInvoiceId) {
            const blob = await downloadInvoicePdf(invoice.id);
            
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `invoice-${invoice.invoiceNumber}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            // Show success message
            loadingToast.className = "fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50";
            loadingToast.innerHTML = "✓ PDF downloaded successfully!";
            setTimeout(() => loadingToast.remove(), 2000);
            return;
          }
        } catch (fetchError) {
          console.error("Error fetching fresh invoice data:", fetchError);
        }
        
        // If still no Zoho invoice ID, show message
        loadingToast.remove();
        alert("This invoice is not available in Zoho. Please contact support.");
      }
    } catch (error) {
      console.error("Error downloading invoice PDF from Zoho:", error);
      loadingToast.className = "fixed top-4 right-4 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg z-50";
      loadingToast.innerHTML = "✗ Failed to download PDF";
      setTimeout(() => loadingToast.remove(), 3000);
      alert("Failed to download invoice from Zoho. Please try again or contact support.");
    }
  };

  // Handle opening invoice in Zoho viewer
  const handleViewInZoho = async (invoice: Invoice) => {
    try {
      if (invoice.zohoInvoiceId) {
        setShowZohoViewer(invoice);
      } else {
        alert("This invoice is not available in Zoho.");
      }
    } catch (error) {
      console.error("Error opening Zoho invoice:", error);
      alert("Failed to open invoice in Zoho.");
    }
  };

  // Handle opening Zoho invoice in external tab
  const handleOpenInZohoTab = async (invoice: Invoice) => {
    try {
      if (invoice.zohoInvoiceId) {
        const zohoUrl = getZohoInvoiceUrl(invoice.zohoInvoiceId, invoice.zohoInvoicePdfUrl);
        window.open(zohoUrl, "_blank");
      } else {
        alert("This invoice is not available in Zoho.");
      }
    } catch (error) {
      console.error("Error opening Zoho invoice:", error);
      alert("Failed to open invoice in Zoho.");
    }
  };


  // Calculate total metrics
  const totalPendingAmount = invoices?.filter(i => i.status?.name === "Unpaid" || i.status?.id === InvoiceStatusEnum.Unpaid)
    .reduce((acc, curr) => acc + (curr.amount || 0), 0) || 0;
  const totalPaidAmount = invoices?.filter(i => i.status?.name === "Paid" || i.status?.id === InvoiceStatusEnum.Paid)
    .reduce((acc, curr) => acc + (curr.amount || 0), 0) || 0;
  const totalOverdueAmount = invoices?.filter(i => i.status?.name === "Overdue" || i.status?.id === InvoiceStatusEnum.Overdue)
    .reduce((acc, curr) => acc + (curr.amount || 0), 0) || 0;

  return (
    <div className="min-h-screen bg-gray-50"  style={{ textAlign: "left" }}>
      <AdminHeader />
      
      <main className="flex-1 lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
                <p className="text-gray-600 mt-1">Manage and generate professional invoices</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Export All
                </Button>
                <Button className="flex items-center gap-2" onClick={() => setShowGenerateModal(true)}>
                  <Plus className="h-4 w-4" />
                  Generate Invoice
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Total Invoiced</p>
                    <p className="text-2xl font-semibold">${(totalPendingAmount + totalPaidAmount).toLocaleString()}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Pending Payment</p>
                    <p className="text-2xl font-semibold">${totalPendingAmount.toLocaleString()}</p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Clock className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Paid</p>
                    <p className="text-2xl font-semibold">${totalPaidAmount.toLocaleString()}</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Overdue</p>
                    <p className="text-2xl font-semibold">${totalOverdueAmount.toLocaleString()}</p>
                  </div>
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertCircleIcon className="h-5 w-5 text-red-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Search and Filters */}
            <Card className="p-4">
  <div className="flex flex-col lg:flex-row justify-between gap-4">
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto">
      <div className="relative flex-1 w-full sm:w-[300px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search invoices..."
          value={invoicesSearchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border rounded-lg"
        />
      </div>
      <select
        value={selectedInvoiceName}
        onChange={(e) => {
          setSelectedInvoiceName(e.target.value);
          setCurrentPage(1);
        }}
        className="px-4 py-2 border rounded-lg w-full sm:w-auto"
      >
        <option value="all">All Invoice Types</option>
        {invoiceNames.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>
      <Button
        variant="outline"
        onClick={() => setShowFilters(!showFilters)}
        className="flex items-center gap-2 w-full sm:w-auto justify-center"
      >
        <Filter className="h-5 w-5" />
        Filters
      </Button>
    </div>
    
    <div className="flex flex-col sm:flex-row items-stretch gap-4 w-full lg:w-auto">
      <select
        value={dateRange}
        onChange={(e) => setDateRange(e.target.value)}
        className="px-4 py-2 border rounded-lg w-full sm:w-auto"
      >
        <option value="week">This Week</option>
        <option value="month">This Month</option>
        <option value="quarter">This Quarter</option>
        <option value="year">This Year</option>
      </select>

      <select
        value={sortBy}
        onChange={(e) => setSortBy(e.target.value)}
        className="px-4 py-2 border rounded-lg w-full sm:w-auto"
      >
        <option value="newest">Newest First</option>
        <option value="oldest">Oldest First</option>
        <option value="amount_high">Highest Amount</option>
        <option value="amount_low">Lowest Amount</option>
      </select>
    </div>
  </div>

  {/* Advanced Filters */}
  {showFilters && (
    <div className="mt-4 pt-4 border-t">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Professional Type
          </label>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg"
          >
            <option value="all">All Types</option>
            <option value="local">Local Professionals</option>
            <option value="international">International Professionals</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg"
          >
            <option value="all">All Status</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="overdue">Overdue</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Currency
          </label>
          <select className="w-full px-4 py-2 border rounded-lg">
            <option value="all">All Currencies</option>
            <option value="CAD">CAD</option>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
          </select>
        </div>
      </div>
    </div>
  )}
</Card>

            {/* Invoices Table */}
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Invoice
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Professional
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invoicesLoading ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-8 text-center">
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <span className="ml-2">Loading invoices...</span>
                          </div>
                        </td>
                      </tr>
                    ) : invoices?.length > 0 ? (
                      invoices.map((invoice: Invoice) => (
                        <tr key={invoice.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <FileText className="h-5 w-5 text-gray-400 mr-2" />
                              <div className="font-medium text-gray-900">{invoice.invoiceNumber}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="font-medium">
                                {`${invoice.receiver?.firstName || ""} ${invoice.receiver?.lastName || ""}`.trim() || "N/A"}
                              </div>
                              <div className="text-sm text-gray-500">{invoice.receiver?.email || ""}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="font-medium">
                                {`${invoice.sender?.firstName || ""} ${invoice.sender?.lastName || ""}`.trim() || "N/A"}
                              </div>
                              <div className="text-sm text-gray-500">{invoice.sender?.email || ""}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm font-medium ${isCreditNote(invoice) ? "text-red-600" : "text-gray-900"}`}>
                              {formatInvoiceAmount(invoice)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge variant={undefined} className={
                              invoice.status?.name === "Paid" || invoice.status?.id === InvoiceStatusEnum.Paid ? "bg-green-100 text-green-700" :
                              invoice.status?.name === "Unpaid" || invoice.status?.id === InvoiceStatusEnum.Unpaid ? "bg-yellow-100 text-yellow-700" :
                              "bg-red-100 text-red-700"
                            }>
                              {invoice.status?.name?.toUpperCase() || "UNKNOWN"}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(invoice.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge variant="outline" className={
                              (invoice as any).zohoInvoiceId 
                                ? "border-green-200 text-green-700" 
                                : "border-orange-200 text-orange-700"
                            }>
                              {(invoice as any).zohoInvoiceId ? "ZOHO" : "LOCAL"}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center gap-2 justify-end">
                              {(invoice as any).zohoInvoiceId && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewInZoho(invoice)}
                                  className="text-green-600 hover:text-green-900"
                                  title="View PDF"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadPDF(invoice)}
                                className="text-gray-600 hover:text-gray-900"
                                title="Download PDF"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                          No invoices found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 border-t flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">
                    Showing page {invoicesCurrentPage} of {Math.ceil((invoicesTotalCount || 0) / 10)}
                  </span>
                  <span className="text-sm text-gray-500">
                    ({invoicesTotalCount || 0} total invoices)
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={!invoicesHasPreviousPage || invoicesLoading}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={!invoicesHasNextPage || invoicesLoading}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </main>

      {/* Modals */}
      <ZohoInvoiceViewer
        invoice={showZohoViewer}
        isOpen={!!showZohoViewer}
        onClose={() => setShowZohoViewer(null)}
      />

      <AddInvoice
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
      />

      {/* <SharedFooter3 /> */}
    </div>
  );
};

export default InvoicesPage;